import React from 'react';
import { motion } from 'framer-motion';

const PartnersSection = () => {
  const partners = [
    { id: 1, name: 'JaliMindCare, Kenya' },
    { id: 2, name: 'Lagos University Teaching Hospital' },
    { id: 3, name: 'St. Dominic Savio Hospital, Nigeria' },
  ];

  return (
    <section id="partners" className="py-24 bg-black text-white">
      <div className="container-custom">
        <div className="text-center mb-16">
          <motion.span
            className="section-tag-dark"
            initial={{ opacity: 0, y: -20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Our Partners
          </motion.span>
          
          <motion.h2 
            className="section-heading-light mt-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Validated With Real Clinical Partners
          </motion.h2>

          <motion.p
            className="section-subheading text-gray-300"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Closed clinical validation across five institutions in Nigeria and Kenya —
            an 80% agreement rate between our screening output and independent clinical assessment.
          </motion.p>
        </div>

        {/* Partners grid - ZemedicAI.ai style */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 mb-24">
          {partners.map((partner, index) => (
            <motion.div
              key={partner.id}
              className="flex items-center justify-center h-28 px-8 rounded-xl border border-gray-800 hover:border-purple-800 transition-all duration-300 bg-gray-900/50"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <span className="text-lg font-medium text-gray-300 text-center">{partner.name}</span>
            </motion.div>
          ))}
        </div>
        
        {/* Case studies - ZemedicAI.ai style */}
        <div className="mb-20">
          <div className="flex items-center justify-center mb-12">
            <span className="h-0.5 w-16 bg-purple-900 mr-6"></span>
            <h3 className="text-2xl font-bold text-white">Where we stand today</h3>
            <span className="h-0.5 w-16 bg-purple-900 ml-6"></span>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                title: "Live in Production",
                location: "Nigeria & Kenya",
                image: "/images/cxr-after.jpg",
                description: "Real inference, uncertainty quantification, and a mandatory clinician sign-off workflow running today — not a slide-deck prototype."
              },
              {
                title: "80% Clinician Agreement",
                location: "National Hospital, Abuja",
                image: "/images/zemedicai-booth-2.png",
                description: "A 200-case clinician-adjudicated sample showed 80% agreement between our AI screening output and independent clinical assessment."
              },
              {
                title: "Scaling to Pilot Across 5 Sites",
                location: "Nigeria & Kenya",
                image: "/images/zemedicai-booth.png",
                description: "Extending clinician-adjudicated validation from a closed 200-case sample to pilot scale across all five partner institutions."
              }
            ].map((study, index) => (
              <motion.div
                key={index}
                className="case-study h-80 rounded-2xl overflow-hidden shadow-lg border border-gray-800"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
              >
                <img 
                  src={study.image} 
                  alt={study.title}
                  className="w-full h-full object-cover"
                />
                <div className="case-study-overlay"></div>
                <div className="case-study-content">
                  <div className="text-purple-300 text-sm mb-2">{study.location}</div>
                  <h4 className="text-white text-xl font-bold mb-2">{study.title}</h4>
                  <p className="text-gray-300 text-sm">{study.description}</p>
                  <a href="#" className="mt-4 inline-flex items-center text-purple-300 hover:text-purple-200 text-sm font-medium">
                    Read case study
                    <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        
        {/* Final CTA - ZemedicAI.ai style */}
        <motion.div
          className="bg-gradient-primary rounded-3xl overflow-hidden text-white py-16 px-8 text-center border border-gray-800"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to transform healthcare delivery in Africa?</h2>
            <p className="text-xl text-gray-200 mb-8">
              Join us as we take AI-assisted diagnostics from clinical validation to scale across Africa.
            </p>
            <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a href="#" className="btn btn-white text-center uppercase tracking-wide text-sm">
                Get Started
              </a>
              <a href="#" className="btn btn-outline border-white text-white hover:bg-white/10 text-center uppercase tracking-wide text-sm">
                Request Demo
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default PartnersSection;